// engine
import {
  ArContainer,
  ArLayout,
  FIXED_DIRECTION,
  gameDispatcher,
  helper,
  ORIENTATION,
  screenDispatcher
} from '@aceroll/core';

// constants
import { GAME_VIEW } from '../constants/game';

// game objects
import { Background } from '../game-objects/background';
import { DebugArea } from '../game-objects/debug-area';
import { Fireworks } from '../game-objects/fireworks';
import { HealthBar } from '../game-objects/health-bar';
import { MultiplierPanel } from '../game-objects/multiplier-panel';
import { SlotMachine } from '../game-objects/slot/slot-machine';
import { WinLines } from '../game-objects/win-lines';

import { BigWinPopup } from '../game-objects/popups/big-win-popup';
import { FeatureEnterPopup } from '../game-objects/popups/feature-enter-popup';
import { FeatureExitPopup } from '../game-objects/popups/feature-exit-popup';
import TotalWin from '../game-objects/total-win';

// mockup
import { Mockup } from '../game-objects/mockup';
import { AdaptiveLogo } from '../game-objects/adaptive-logo';

export default class Game extends ArLayout {
	logoContainer = null;
	bgContainer = null;
	gameContainer = null;

	background = null;

	multiplierPanel = null;
	healthBar = null;
	slotMachine = null;
	winLines = null;
	fireworks = null;
	totalWin = null;

	featureEnterPopup = null;
	featureExitPopup = null;
	bigWinPopup = null;

	constructor(name) {
		super(name);

		this.onGameTypeChange = this.onGameTypeChange.bind(this);
	}

	// lifecycle
	init() {
		super.init();

		this.#setupBackground();

		if (helper.debug) {
			this.#setupDebug();
		}

		this.#setupGame();
		this.#setupPopups();
	}

	#setupBackground() {
		this.bgContainer = new ArContainer('bgContainer');
		this.bgContainer.scaleMode = 'max';
		this.bgContainer.baseSize = {
			[ORIENTATION.LANDSCAPE]: {
				width: 2048,
				height: 945
			},
			[ORIENTATION.PORTRAIT]: {
				width: 945,
				height: 2048
			}
		};
		this.bgContainer.baseAnchor = {
			[ORIENTATION.LANDSCAPE]: {
				x: 0.5,
				y: 0.5
			},
			[ORIENTATION.PORTRAIT]: {
				x: 0.5,
				y: 0
			}
		};

		this.background = new Background();
		this.bgContainer.addChild(this.background);
		this.root.addChild(this.bgContainer);
	}

	#setupDebug() {
		this.debugArea = new DebugArea();
		this.debugArea.scaleMode = 'min';
		this.debugArea.visible = false;
		this.debugArea.baseSize = {
			[ORIENTATION.LANDSCAPE]: {
				width: 860,
				height: 1080
			},
			[ORIENTATION.PORTRAIT]: {
				width: 1080,
				height: 1260
			}
		};


		this.mockup = new Mockup();
		this.mockup.scaleMode = 'min';
		this.mockup.baseSize = {
			[ORIENTATION.LANDSCAPE]: {
				width: 860,
				height: 1080
			},
			[ORIENTATION.PORTRAIT]: {
				width: 1080,
				height: 1260
			}
		};
		this.mockup.setup();

		this.root.addChild(this.debugArea, this.mockup);
	}

	#setupGame() {
		this.gameContainer = new ArContainer('gameContainer');
		this.gameContainer.scaleMode = 'min';
		this.gameContainer.baseSize = {
			[ORIENTATION.LANDSCAPE]: {
				width: 860,
				height: 1080
			},
			[ORIENTATION.PORTRAIT]: {
				width: 1180,
				height: 1360
			}
		};

		this.slotMachine = new SlotMachine();
    this.logo = new AdaptiveLogo(this.slotMachine);
    this.multiplierPanel = new MultiplierPanel();
		this.healthBar = new HealthBar();
		this.winLines = new WinLines();
		this.fireworks = this.slotMachine.addChild(new Fireworks());
		this.gameContainer.fixElement(this.logo, FIXED_DIRECTION.TOP_LEFT, { x: 20, y: 50 });
		this.totalWin = new TotalWin();

		this.gameContainer.addChild(
			this.healthBar,
			this.multiplierPanel,
			this.slotMachine,
			this.logo,
			this.winLines,
			this.totalWin
		);

		this.root.addChild(this.gameContainer);
	}

	#setupPopups() {
		this.featureEnterPopup = new FeatureEnterPopup();
		this.featureExitPopup = new FeatureExitPopup();
		this.bigWinPopup = new BigWinPopup();

		this.root.addChild(
			this.featureEnterPopup,
			this.featureExitPopup,
			this.bigWinPopup
		);
	}

	onSet() {
		this.#setEvents();
	}

	onUnset() {
		this.#removeEvents();
	}

	// change view
	setMainView() {
		this.background.setMainView();
    this.#alignSlotMachineElementsForOrientation();
		this.slotMachine.exitBonus();
	}

	setBonusView() {
		this.background.setBonusView();
    this.#alignSlotMachineElementsForOrientation();
		this.slotMachine.onBonus();
	}

	// change orientation
	onLandscape() {
		if (helper.debug) {
			this.debugArea.onLandscape();
		}

		this.background.onLandscape();
		this.slotMachine.onOrientationChange('landscape');
    this.healthBar.position.set(232, 89);
    this.healthBar.scale.set(1);
    this.totalWin.position.set(430, 542);
    this.totalWin.scale.set(0.73);
    this.fireworks.position.set(30, 480);

    this.#alignSlotMachineElementsForLandscape();

		this.logo.scale.set(0.6);
    this.logo.onLandscape();
		this.featureEnterPopup.onLandscape();
		this.featureExitPopup.onLandscape();
		this.bigWinPopup.onLandscape();
	}

	onPortrait() {
		if (helper.debug) {
			this.debugArea.onPortrait();
		}

		this.background.onPortrait();

		this.slotMachine.onOrientationChange('portrait');
    this.healthBar.position.set(342, 80);
    this.healthBar.scale.set(1.25);
    this.totalWin.position.set(540, 676);
    this.totalWin.scale.set(1);
    this.fireworks.position.set(30, 480);

   this.#alignSlotMachineElementsForPortrait();

		this.logo.scale.set(0.6);
    this.logo.onPortrait();
		this.featureEnterPopup.onPortrait();
		this.featureExitPopup.onPortrait();
		this.bigWinPopup.onPortrait();
	}

	// inner
	#setEvents() {
		gameDispatcher.on('gameView', this.onGameTypeChange);
	}

	#removeEvents() {
		gameDispatcher.off('gameView', this.onGameTypeChange);
	}

	// events
	onGameTypeChange(gameType) {
		switch (gameType) {
			case GAME_VIEW.MAIN:
				this.setMainView();
				break;
			case GAME_VIEW.BONUS:
				this.setBonusView();
				break;
		}
	}

  #alignSlotMachineElementsForOrientation() {
    if (screenDispatcher.state.orientation === ORIENTATION.LANDSCAPE) {
      this.#alignSlotMachineElementsForLandscape();
    } else {
      this.#alignSlotMachineElementsForPortrait();
    }
  }

  #alignSlotMachineElementsForLandscape() {
    if (gameDispatcher.state.gameView === GAME_VIEW.MAIN) {
      this.slotMachine.scale.set(0.80);
      this.slotMachine.position.set(16, 90);

      this.multiplierPanel.scale.set(0.79);
      this.multiplierPanel.position.set(114, 80);
    } else {
      this.slotMachine.scale.set(0.73);
      this.slotMachine.position.set(62, 188);

      this.multiplierPanel.position.set(150, 178);
      this.multiplierPanel.scale.set(0.72);
    }
  }

  #alignSlotMachineElementsForPortrait() {
    if (gameDispatcher.state.gameView === GAME_VIEW.MAIN) {
      this.slotMachine.position.set(86, 194);
      this.multiplierPanel.position.set(203, 177);
    } else {
      this.slotMachine.position.set(86, 214);
      this.multiplierPanel.position.set(203, 197);
    }
    this.slotMachine.scale.set(1);
    this.multiplierPanel.scale.set(1);
  }
}
